import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { LocationsPort } from '../../../app/ports/organization/locations.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastV2Service, CoreUIToastDirection } from '../../services/toast.service';
import { OrgSdk } from 'lib-klab-sdks';
import { Location } from '../../../domain/models/organization/location.model';
import { PageMeta } from '../../../domain/models/organization/page.model';
import { mapSDKLocationToDomain } from '../../mappers/organization/location.mapper';
import { mapSdkPaginationToDomainPageMeta } from '../../mappers/organization/page.mapper';
import { mapOrganizationErrorToMessage } from '../../utils/organization-error.mapper';
import { UpdateLocationRequest } from '../../../domain/models/organization/request/update-location.request';
import { CreateLocationRequest } from '../../../domain/models/organization/request/create-location.request';
import { CrudMockAction } from '../../config/crud-mock-action.type';
import { EnvMicroOrganization } from '../../config/env.micro.organization';
import { ORG_ENV } from '../../config/micros-env.tokens';
import { locationsSdkMock } from '../../mocks/organization/locations.mock';

@Injectable({ providedIn: 'root' })
export class LocationsPortAdapter implements LocationsPort {
    constructor(
        private locationsApi: OrgSdk.LocationsService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(ORG_ENV) private env: EnvMicroOrganization,
    ) { }

    getLocations(page: number, size: number): Observable<{ items: Location[]; page: PageMeta }> {
        this.appEvents.loaderVisiblePublish(true, 'locations');

        const source$ = this.shouldMock('getAll')
            ? of(locationsSdkMock.getAllLocations(page, size)).pipe(delay(360))
            : this.locationsApi.getAllLocations(page, size);

        return source$.pipe(
            map((resp) => {
                const pageData = Array.isArray(resp) ? resp[0] : resp;
                return {
                    items: (pageData?.content ?? []).map(mapSDKLocationToDomain),
                    page: mapSdkPaginationToDomainPageMeta(pageData?.page),
                };
            }),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'locations')),
        );
    }

    getLocationById(locationId: number): Observable<Location> {
        this.appEvents.loaderVisiblePublish(true, 'locations-getById');

        const source$: Observable<any> = this.shouldMock('getById')
            ? of(locationsSdkMock.getLocationById(locationId)).pipe(delay(360))
            : this.locationsApi.getLocationById(locationId);

        return source$.pipe(
            map((resp: any) => mapSDKLocationToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'locations-getById')),
        );
    }

    createLocation(payload: CreateLocationRequest): Observable<Location> {
        this.appEvents.loaderVisiblePublish(true, 'locations-create');

        const sdkPayload: OrgSdk.LocationCreateRequest = {
            name: payload.name,
            departments: payload.departments,
        };

        const source$: Observable<any> = this.shouldMock('create')
            ? of(locationsSdkMock.createLocation(sdkPayload)).pipe(delay(360))
            : this.locationsApi.createLocation(sdkPayload);

        return source$.pipe(
            map((resp: any) => mapSDKLocationToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'locations-create')),
        );
    }

    updateLocation(locationId: number, payload: UpdateLocationRequest): Observable<Location> {
        this.appEvents.loaderVisiblePublish(true, 'locations-update');

        const sdkPayload: OrgSdk.LocationUpdateRequest = {
            name: payload.name,
            departments: payload.departments.map((d) => ({
                ...(d.departmentId ? { departmentId: d.departmentId } : {}),
                name: d.name,
                action: this.toSdkDeptAction(d.action),
            })),
        };

        const source$: Observable<any> = this.shouldMock('update')
            ? of(locationsSdkMock.updateLocation(locationId, sdkPayload)).pipe(delay(360))
            : this.locationsApi.updateLocation(locationId, sdkPayload);

        return source$.pipe(
            map((resp: any) => mapSDKLocationToDomain(resp as OrgSdk.LocationResponse)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'locations-update')),
        );
    }

    deleteLocation(locationId: number): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'locations-delete');

        const source$: Observable<any> = this.shouldMock('delete')
            ? of(locationsSdkMock.deleteLocation(locationId)).pipe()
            : this.locationsApi.deleteLocationById(locationId);

        return source$.pipe(
            map(() => void 0),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'locations-delete')),
        );
    }

    private toSdkDeptAction(action?: 'ADD' | 'DELETE') {
        if (!action) return undefined;
        return action === 'ADD'
            ? OrgSdk.DepartmentUpdateItem.ActionEnum.Add
            : OrgSdk.DepartmentUpdateItem.ActionEnum.Delete;
    }

    private shouldMock(action: CrudMockAction): boolean {
        return Boolean(
            this.env?.useMocks ||
            this.env?.mock?.locations?.[action]
        );
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapOrganizationErrorToMessage(err),
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
